import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 20;

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [tx, setTx] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setTx(r.data);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (r.data.payment_status === "expired" || r.data.payment_status === "failed") { setState("error"); return; }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) { setState("timeout"); return; }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="payment-success-page">
      {state === "polling" && (
        <>
          <Loader2 className="w-10 h-10 text-primary mx-auto animate-spin" />
          <h1 className="font-display text-3xl mt-6">Confirming payment…</h1>
          <p className="text-muted-foreground mt-2">This usually takes a few seconds.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-primary/10 grid place-items-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <div className="eyebrow mt-6">Payment received</div>
          <h1 className="font-display text-4xl mt-2" data-testid="payment-success-title">You&apos;re live.</h1>
          <p className="text-muted-foreground mt-3">
            Your <b>{tx?.package}</b> listing has been marked as paid. Our team will publish it within 24 hours.
          </p>
          <div className="flex justify-center gap-3 mt-8">
            <Button asChild className="rounded-full bg-primary hover:bg-primary/90" data-testid="success-listings">
              <Link to="/listings">Browse listings <ArrowRight className="w-4 h-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-border">
              <Link to="/">Home</Link>
            </Button>
          </div>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <div className="w-16 h-16 rounded-full bg-destructive/10 grid place-items-center mx-auto">
            <XCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="font-display text-3xl mt-6">Couldn&apos;t confirm payment</h1>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            {state === "timeout"
              ? "This is taking longer than expected. Check your email — Stripe will confirm shortly. If your card was charged, our team will publish your listing."
              : "Something went wrong. Please contact our team and we'll sort it out."}
          </p>
          <div className="flex justify-center gap-3 mt-8">
            <Button asChild className="rounded-full bg-primary hover:bg-primary/90">
              <Link to="/contact">Contact support</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-border">
              <Link to="/">Home</Link>
            </Button>
          </div>
        </>
      )}
    </main>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <main className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="payment-cancel-page">
      <div className="w-16 h-16 rounded-full bg-muted grid place-items-center mx-auto">
        <XCircle className="w-8 h-8 text-muted-foreground" />
      </div>
      <h1 className="font-display text-3xl mt-6">Payment cancelled</h1>
      <p className="text-muted-foreground mt-2">Your listing is still saved as pending. Complete payment anytime.</p>
      <div className="flex justify-center gap-3 mt-8">
        <Button onClick={() => navigate("/sell")} className="rounded-full bg-primary hover:bg-primary/90" data-testid="cancel-retry">
          Try again
        </Button>
        <Button asChild variant="outline" className="rounded-full border-border">
          <Link to="/">Home</Link>
        </Button>
      </div>
    </main>
  );
}
